/**
 * Hook for subscribing to FOS processing real-time updates.
 *
 * Provides a live feed of FOS decision processing events including:
 * - Sync batch started/completed
 * - Individual decision processing stages (summarizing, embedding)
 * - Success and failure events
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { realtimeClient, RealtimeMessage } from '../services/RealtimeClient';

export interface FOSLogEntry {
  id: string;
  timestamp: Date;
  type: 'started' | 'progress' | 'completed' | 'failed';
  level: 'sync' | 'decision';
  message: string;
    data: {
    decisionId?: string;
    reference?: string;
    stage?: string;
    error?: string;
    count?: number;
    processingTimeMs?: number;
    batchSize?: number;
    totalPending?: number;
    processed?: number;
    total?: number;
    percent?: number;
    decisionsFound?: number;
    decisionsImported?: number;
    decisionsSkipped?: number;
    pagesScraped?: number;
    pdfsDownloaded?: number;
    scrapeErrors?: number;
    durationSeconds?: number;
    pageUrl?: string;
    pageNumber?: number;
    pageStartIndex?: number;
    totalPages?: number;
    pageDecisions?: number;
    decisionsFetched?: number;
  };
}

interface UseFOSProcessingLogOptions {
  maxEntries?: number;
  autoConnect?: boolean;
  /**
   * Callback fired on any FOS processing event.
   * Useful for triggering data refetches.
   */
  onEvent?: (entry: FOSLogEntry) => void;
}

interface UseFOSProcessingLogReturn {
  entries: FOSLogEntry[];
  isProcessing: boolean;
  clearLog: () => void;
  stats: {
    total: number;
    completed: number;
    failed: number;
    inProgress: number;
  };
}

// Map message types to our internal entry types
const MESSAGE_TYPE_MAP: Record<string, { type: FOSLogEntry['type']; level: FOSLogEntry['level'] }> = {
  'fos.sync.started': { type: 'started', level: 'sync' },
  'fos.sync.progress': { type: 'progress', level: 'sync' },
  'fos.sync.completed': { type: 'completed', level: 'sync' },
  'fos.sync.failed': { type: 'failed', level: 'sync' },
  'fos.decision.started': { type: 'started', level: 'decision' },
  'fos.decision.progress': { type: 'progress', level: 'decision' },
  'fos.decision.completed': { type: 'completed', level: 'decision' },
  'fos.decision.failed': { type: 'failed', level: 'decision' },
};

// FOS message types to subscribe to
const FOS_MESSAGE_TYPES = [
  'fos.sync.started',
  'fos.sync.progress',
  'fos.sync.completed',
  'fos.sync.failed',
  'fos.decision.started',
  'fos.decision.progress',
  'fos.decision.completed',
  'fos.decision.failed',
];

/**
 * Formats a message for display in the log
 */
function formatLogMessage(type: string, data: RealtimeMessage['data']): string {
  switch (type) {
    case 'fos.sync.started':
      if ((data?.batch_size ?? 0) === 0 && (data?.total_pending ?? 0) === 0) {
        return 'Started FOS sync';
      }
      return `Started processing batch of ${data?.batch_size ?? 0} decisions (${data?.total_pending ?? 0} pending total)`;
    case 'fos.sync.progress':
      {
        const stage = data?.stage ?? 'Sync progress';
        const parts = [];
        if (data?.decisions_found !== undefined) {
          parts.push(`${data.decisions_found} found`);
        }
        if (data?.decisions_imported !== undefined) {
          parts.push(`${data.decisions_imported} imported`);
        }
        if (data?.decisions_skipped !== undefined) {
          parts.push(`${data.decisions_skipped} skipped`);
        }
        if (data?.pages_scraped !== undefined) {
          parts.push(`${data.pages_scraped} pages`);
        }
        if (data?.pdfs_downloaded !== undefined) {
          parts.push(`${data.pdfs_downloaded} pdfs`);
        }
        if (data?.errors !== undefined) {
          parts.push(`${data.errors} errors`);
        }
        if (data?.page_decisions !== undefined) {
          parts.push(`${data.page_decisions} decisions`);
        }
        if (data?.decisions_fetched !== undefined) {
          parts.push(`${data.decisions_fetched} total fetched`);
        }

        const pageLabel = data?.page_number
          ? `Page ${data.page_number}${data.total_pages ? `/${data.total_pages}` : ''}`
          : null;
        const startLabel = data?.page_start_index !== undefined
          ? `Start=${data.page_start_index}`
          : null;
        const pageInfo = [pageLabel, startLabel].filter(Boolean).join(' • ');

        const progress = data?.total
          ? `${data?.processed ?? 0}/${data.total} (${data?.percent ?? 0}%)`
          : null;

        if (pageInfo) {
          return `${stage}: ${pageInfo}${parts.length > 0 ? ` • ${parts.join(', ')}` : ''}${progress ? ` • ${progress}` : ''}`;
        }

        if (parts.length > 0) {
          return `${stage}: ${parts.join(', ')}${progress ? ` • ${progress}` : ''}`;
        }

        return progress ? `${stage}: ${progress}` : stage;
      }
    case 'fos.sync.completed': {
      const parts = [];
      if (data?.decisions_found !== undefined) {
        parts.push(`${data.decisions_found} found`);
      }
      if (data?.decisions_imported !== undefined) {
        parts.push(`${data.decisions_imported} imported`);
      }
      if (data?.decisions_skipped !== undefined) {
        parts.push(`${data.decisions_skipped} skipped`);
      }
      const duration = data?.duration_seconds ? ` in ${data.duration_seconds.toFixed(1)}s` : '';
      if (parts.length > 0) {
        return `Sync completed: ${parts.join(', ')}${duration}`;
      }
      return `Sync completed${duration}`;
    }
    case 'fos.sync.failed':
      return `Sync failed: ${data?.error ?? 'Unknown error'}`;
    case 'fos.decision.started':
      return `${data?.reference ?? 'Unknown'} ${data?.stage ?? 'processing'}...`;
    case 'fos.decision.progress':
      return `${data?.reference ?? 'Unknown'} ${data?.stage ?? 'processing'}${data?.detail ? `: ${data.detail}` : ''}`;
    case 'fos.decision.completed': {
      const timeStr = data?.processing_time_ms
        ? ` (${(data.processing_time_ms / 1000).toFixed(1)}s)`
        : '';
      return `${data?.reference ?? 'Unknown'} ${data?.stage ?? 'completed'} successfully${timeStr}`;
    }
    case 'fos.decision.failed':
      return `${data?.reference ?? 'Unknown'} failed: ${data?.error ?? 'Unknown error'}`;
    default:
      return `Unknown event: ${type}`;
  }
}

/**
 * Hook for subscribing to FOS processing real-time updates
 */
export function useFOSProcessingLog(
  options: UseFOSProcessingLogOptions = {}
): UseFOSProcessingLogReturn {
  const { maxEntries = 100, autoConnect = true, onEvent } = options;

  const [entries, setEntries] = useState<FOSLogEntry[]>([]);
  const entriesRef = useRef<FOSLogEntry[]>([]);
  const entryIdCounter = useRef(0);
  const activeDecisions = useRef<Set<string>>(new Set());

  // Track if processing is ongoing
  const [isProcessing, setIsProcessing] = useState(false);

  // Handle incoming messages
  const handleMessage = useCallback((message: RealtimeMessage) => {
    console.log('[FOS-LOG] Received message:', message.type, message);

    const typeInfo = MESSAGE_TYPE_MAP[message.type];
    if (!typeInfo) {
      console.log('[FOS-LOG] Unknown message type, ignoring:', message.type);
      return;
    }

    const newEntry: FOSLogEntry = {
      id: `fos-log-${Date.now()}-${entryIdCounter.current++}`,
      timestamp: new Date(message.timestamp || Date.now()),
      type: typeInfo.type,
      level: typeInfo.level,
      message: formatLogMessage(message.type, message.data),
      data: {
        decisionId: message.data?.decision_id,
        reference: message.data?.reference,
        stage: message.data?.stage,
        error: message.data?.error,
        count: message.data?.batch_size || message.data?.decisions_imported,
        processingTimeMs: message.data?.processing_time_ms,
        batchSize: message.data?.batch_size,
        totalPending: message.data?.total_pending,
        processed: message.data?.processed,
        total: message.data?.total,
        percent: message.data?.percent,
        decisionsFound: message.data?.decisions_found,
        decisionsImported: message.data?.decisions_imported,
        decisionsSkipped: message.data?.decisions_skipped,
        pagesScraped: message.data?.pages_scraped,
        pdfsDownloaded: message.data?.pdfs_downloaded,
        scrapeErrors: message.data?.errors,
        durationSeconds: message.data?.duration_seconds,
        pageUrl: message.data?.page_url,
        pageNumber: message.data?.page_number,
        pageStartIndex: message.data?.page_start_index,
        totalPages: message.data?.total_pages,
        pageDecisions: message.data?.page_decisions,
        decisionsFetched: message.data?.decisions_fetched,
      },
    };

    // Track active decisions for isProcessing state
    if (typeInfo.level === 'decision') {
      const decisionId = message.data?.decision_id;
      if (decisionId) {
        if (typeInfo.type === 'started' || typeInfo.type === 'progress') {
          activeDecisions.current.add(decisionId);
        } else {
          activeDecisions.current.delete(decisionId);
        }
      }
    }

    // Track sync-level processing
    if (typeInfo.level === 'sync') {
      if (typeInfo.type === 'started') {
        setIsProcessing(true);
      } else if (typeInfo.type === 'completed' || typeInfo.type === 'failed') {
        // Only mark as not processing if no active decisions
        if (activeDecisions.current.size === 0) {
          setIsProcessing(false);
        }
      }
    }

    // Update processing state based on active decisions
    setIsProcessing(activeDecisions.current.size > 0);

    // Add entry and maintain max limit
    setEntries(prev => {
      const updated = [newEntry, ...prev].slice(0, maxEntries);
      entriesRef.current = updated;
      return updated;
    });

    // Fire callback on any event
    onEvent?.(newEntry);
  }, [maxEntries, onEvent]);

  // Subscribe to FOS events
  useEffect(() => {
    if (!autoConnect) return;

    console.log('[FOS-LOG] Subscribing to fos:sync channel');

    const unsubscribe = realtimeClient.subscribe(
      'fos',
      'sync',
      handleMessage,
      { messageTypes: FOS_MESSAGE_TYPES }
    );

    console.log('[FOS-LOG] Subscription established');

    return () => {
      console.log('[FOS-LOG] Unsubscribing from fos:sync channel');
      unsubscribe();
    };
  }, [handleMessage, autoConnect]);

  // Clear log function
  const clearLog = useCallback(() => {
    setEntries([]);
    entriesRef.current = [];
    activeDecisions.current.clear();
    setIsProcessing(false);
  }, []);

  // Calculate stats
  const stats = {
    total: entries.length,
    completed: entries.filter(e => e.type === 'completed').length,
    failed: entries.filter(e => e.type === 'failed').length,
    inProgress: activeDecisions.current.size,
  };

  return {
    entries,
    isProcessing,
    clearLog,
    stats,
  };
}

export default useFOSProcessingLog;
